import type { ComponentPropsWithoutRef, Ref } from 'react';
import { cva } from 'class-variance-authority';
import { Check, X } from 'lucide-react';
import type { LabelColour } from '@molt/shared';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { labelColourDisplayName, labelColourName, labelSwatch } from './palette';

/**
 * A board label, rendered — `FE-04.10`; `docs/design/stage2/card-back.md`'s
 * `LabelChip` row, D8 and D10, and `finding-things.md`'s label facet.
 *
 * Used by `FE-15.1` (the label popover), `FE-15.2` (labels on a card), the
 * board filter panel's label facet (`FE-19.3`) and the value slot of a
 * `FilterChip`. All four render the same thirty-one colours, which is why the
 * palette is `palette.ts` and not a constant in whichever of them shipped
 * first.
 *
 * ── Always a full pill carrying the name ──────────────────────────────────
 * Paper's own argument: the colour is redundant, not load-bearing, so a chip
 * says what it is in words. `labelSchema.name` is nevertheless nullable and a
 * new board seeds six **unnamed** coloured labels, so `name={null}` renders a
 * pill whose visible content is empty and whose accessible name is its colour
 * word — "Subtle green label, no name". Never a blank pill, and never an
 * unlabelled control.
 *
 * ── `colourless` is a fourth visual tier, not a colour ────────────────────
 * `labels.colour` is `NOT NULL`, so "no colour" has exactly one spelling. It
 * renders as an opaque `--color-surface-raised` pill with a
 * `--color-border-strong` edge, and it is the only chip in the set that takes
 * an edge — without one it is an invisible pill rather than a quiet one.
 *
 * ⚠ **A chip takes no border and a palette SWATCH always does.** They look
 * like one object and are not: a swatch in the label editor's grid is a
 * *control* whose fill is the only thing it says, so 1.4.11 applies to that
 * fill and it fails, in both themes, in opposite tiers
 * (`SWATCH_FILL_VS_SURFACE` in `palette.ts` is the measurement). A chip's edge
 * encodes nothing, and adding one would spend the density budget restating
 * what the text already says. `FE-15.1` builds the swatch grid; **do not build
 * it out of this component.**
 *
 * ── `interactive` and `removable` are mutually exclusive, by type ──────────
 * `card-back.md` lists both props, and the two surfaces that want them are
 * different: the picker's rows are toggles (`interactive` + `selected`), and
 * the card's applied labels carry a remove control (`removable`). Allowing
 * both would nest a `<button>` inside a `<button>`, which is invalid HTML and
 * gives the inner control no reliable keyboard behaviour. The prop type is a
 * union, so a caller cannot express the combination — the compiler says so
 * rather than the DOM saying nothing.
 *
 * A chip is therefore a `<span>` unless `interactive`, in which case it is the
 * `<button>` itself. That matters for `finding-things.md`'s `FacetOption`,
 * which wraps a native `<input type="checkbox">` in a `<label>` and renders a
 * chip beside it: a chip that were always a button could not go there.
 */

/**
 * The remove control sits INSIDE the coloured fill, where the global
 * `--color-accent` focus ring measures under 1.5:1 against a bold swatch —
 * `--color-accent` is `oklch(53% 0.128 38)` and `red_bold` is
 * `oklch(47% 0.150 27)`. The house rule for that case is to ADD a ring, never
 * to remove one, so the control takes an outline in the chip's OWN ink, which
 * `palette.ts` has already measured at 6.10:1 worst case against every fill in
 * the palette. The offset drops to 1px so the ring stays on the fill it was
 * measured against instead of straddling the pill's edge.
 *
 * The chip ITSELF needs none of this: the global ring sits at `outline-offset:
 * 2px`, which lands it outside the pill on the page surface, where
 * `--color-accent` measures 5.37:1.
 */
const REMOVE_FOCUS_RING = {
  dark: 'focus-visible:outline-offset-1 focus-visible:outline-board-ink-dark',
  light: 'focus-visible:outline-offset-1 focus-visible:outline-board-ink-light',
  /** The colourless chip is a theme surface, so the global accent ring is correct on it. */
  theme: '',
} as const;

const labelChipVariants = cva(
  // `border-transparent` rather than no border at all: the colourless chip
  // takes a real 1px edge, and without a placeholder on the other thirty a
  // colourless chip would be 2px taller than the chips beside it.
  'label-chip-pattern relative isolate inline-flex max-w-full items-center gap-1 overflow-hidden rounded-full border border-transparent font-medium leading-none',
  {
    variants: {
      size: {
        sm: 'h-5 gap-0.5 px-2 text-xs',
        md: 'h-6 px-2.5 text-xs',
      },
      /**
       * Which of the two board inks this chip's fill carries — measured per
       * swatch in `palette.ts`, never assumed per tier. `theme` is the
       * colourless treatment, the one value that is an absence of colour and
       * therefore follows the theme like everything else.
       */
      ink: {
        dark: 'text-board-ink-dark',
        light: 'text-board-ink-light',
        theme: 'border-border-strong bg-surface-raised text-ink',
      },
      interactive: {
        true: 'cursor-pointer',
        false: '',
      },
    },
    defaultVariants: { size: 'md', interactive: false },
  },
);

interface LabelChipOwnProps {
  /** One of the 31 `LABEL_COLOURS`. `colourless` renders the fourth treatment. */
  colour: LabelColour;
  /** `labelSchema.name`. `null` is a real wire state — an unnamed seeded label. */
  name: string | null;
  /** Visual-only display mode. The accessible name always keeps `name`. */
  showName?: boolean;
  size?: 'sm' | 'md';
}

type ChipRest<E extends 'span' | 'button'> = Omit<
  ComponentPropsWithoutRef<E>,
  keyof LabelChipOwnProps | 'children'
>;

export type LabelChipProps =
  | (LabelChipOwnProps &
      ChipRest<'span'> & {
        interactive?: false;
        selected?: never;
        /** Renders a nested remove control. Not available on an interactive chip. */
        removable?: boolean;
        onRemove?: () => void;
        /** Overrides the remove control's accessible name. */
        removeLabel?: string;
        ref?: Ref<HTMLSpanElement>;
      })
  | (LabelChipOwnProps &
      ChipRest<'button'> & {
        /** Renders the chip as a toggle — the label picker's row. */
        interactive: true;
        /** Sets `aria-pressed`. Meaningless without `interactive`. */
        selected?: boolean;
        removable?: never;
        onRemove?: never;
        removeLabel?: never;
        ref?: Ref<HTMLButtonElement>;
      });

export type LabelChipTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/** The words a screen reader gets for a chip with no name of its own. */
function accessibleName(
  colour: LabelColour,
  name: string | null,
  t?: LabelChipTranslate,
): string {
  if (name !== null) return name;
  const colourName =
    t === undefined ? labelColourName(colour) : labelColourDisplayName(t, colour);
  return t === undefined
    ? `${colourName} label, no name`
    : t('primitives.labelChip.unnamed', { colour: colourName });
}

function labelPattern(colour: LabelColour): string | undefined {
  if (colour === 'colourless') return undefined;
  return colour.replace(/_(subtle|bold)$/, '');
}

export function LabelChip(props: LabelChipProps) {
  const { t } = useInterfaceLanguage();
  const swatch = labelSwatch(props.colour);
  const ink = swatch?.ink ?? 'theme';
  const pattern = labelPattern(props.colour);
  const patternProps =
    pattern === undefined
      ? {}
      : {
          'data-label-colour': props.colour,
          'data-label-pattern': pattern,
        };

  // The one place a colour literal leaves `palette.ts`. It has to be an inline
  // style: the value is board data read at runtime, so there is no class name
  // that could carry it, and Tailwind has nothing to generate from it.
  const fill = swatch ? { backgroundColor: swatch.fill } : undefined;

  if (props.interactive === true) {
    const {
      interactive: _interactive,
      colour,
      name,
      showName = true,
      size,
      selected = false,
      className,
      ...rest
    } = props;

    return (
      <button
        type="button"
        aria-pressed={selected}
        style={fill}
        className={cn(labelChipVariants({ size, ink, interactive: true }), className)}
        {...patternProps}
        {...rest}
      >
        {name === null || !showName ? (
          <span className="sr-only">{accessibleName(colour, name, t)}</span>
        ) : (
          <span className="relative z-10 truncate">{name}</span>
        )}
        {selected ? (
          <Check aria-hidden="true" className="relative z-10 size-3 shrink-0" />
        ) : null}
      </button>
    );
  }

  const {
    interactive: _interactive,
    colour,
    name,
    showName = true,
    size,
    removable = false,
    onRemove,
    removeLabel,
    className,
    ...rest
  } = props;

  return (
    <span
      style={fill}
      className={cn(labelChipVariants({ size, ink }), className)}
      {...patternProps}
      {...rest}
    >
      {name === null || !showName ? (
        <span className="sr-only">{accessibleName(colour, name, t)}</span>
      ) : (
        <span className="relative z-10 truncate">{name}</span>
      )}
      {removable ? (
        <button
          type="button"
          onClick={onRemove}
          // A local button rather than `IconButton`: that primitive's `quiet`
          // variant is `text-ink-muted` and its `on-scrim` variant is for the
          // dialog scrim, so neither carries the chip's DATA ink — a quiet
          // IconButton on `red_bold` is invisible. Reported as a finding
          // rather than worked around silently; a third IconButton variant
          // that inherits `currentColor` would let this become an import.
          className={cn(
            'relative z-10 shrink-0 cursor-pointer rounded-full opacity-80 hover:opacity-100',
            REMOVE_FOCUS_RING[ink],
            size === 'sm' ? '-mr-1' : '-mr-1.5',
          )}
          aria-label={
            removeLabel ??
            t('primitives.labelChip.remove', {
              label:
                name ??
                t('primitives.labelChip.namedColour', {
                  colour: labelColourDisplayName(t, colour),
                }),
            })
          }
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </span>
  );
}

/**
 * The chip's accessible name, exported for the surfaces that have to compose
 * it into a longer string — a `FilterChip` reading "Label: Urgent", or a card's
 * summary line. Re-deriving it at each call site is how an unnamed label ends
 * up announced as an empty string in one place out of four.
 */
export function labelChipName(
  colour: LabelColour,
  name: string | null,
  t?: LabelChipTranslate,
): string {
  return accessibleName(colour, name, t);
}
